import React, { useState } from 'react';
import Layout from '@/components/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useAuth } from '@/contexts/AuthContext';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import ROIDashboard from '@/components/bi/ROIDashboard';
import FinancialEventsTable from '@/components/bi/FinancialEventsTable';
import OrganizationalStructureComponent from '@/components/bi/OrganizationalStructure';
import InteractionManagement from '@/components/bi/InteractionManagement';
import { 
  TrendingUp, 
  DollarSign, 
  Building2, 
  Calendar as CalendarIcon,
  Users,
  Sparkles,
  Copy
} from 'lucide-react';
import ClientSelectDropdown from '@/components/bi/ClientSelectDropdown';
import CloneSettingsModal from '@/components/bi/CloneSettingsModal';

const BusinessIntelligence: React.FC = () => {
  const { user } = useAuth();
  const [selectedClientId, setSelectedClientId] = useState<number | undefined>(undefined);
  const [startDate, setStartDate] = useState<Date | undefined>(
    new Date(new Date().setMonth(new Date().getMonth() - 1))
  );
  const [endDate, setEndDate] = useState<Date | undefined>(new Date());
  const [startDateOpen, setStartDateOpen] = useState(false);
  const [endDateOpen, setEndDateOpen] = useState(false);
  const [isCloneModalOpen, setIsCloneModalOpen] = useState(false);

  // Determinar clientId baseado no role
  const effectiveClientId = user?.role === 'admin' ? selectedClientId : user?.client_id;

  const startDateStr = startDate ? format(startDate, 'yyyy-MM-dd') : undefined;
  const endDateStr = endDate ? format(endDate, 'yyyy-MM-dd') : undefined;

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50/50">
        <div className="max-w-7xl mx-auto px-6 py-8 space-y-8">
          {/* Header Elegante */}
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 shadow-lg">
                <Sparkles className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Atendimentos IA</h1>
                <p className="text-gray-500 text-sm mt-0.5">Análise inteligente de atendimentos e métricas de negócio</p>
              </div>
            </div>
          </div>

          {/* Filtros Minimalistas */}
          <Card className="border border-gray-200 shadow-sm">
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3 items-center">
                {user?.role === 'admin' && (
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-gray-600 uppercase tracking-wide">
                      Cliente
                    </label>
                    <ClientSelectDropdown
                      value={selectedClientId}
                      onValueChange={setSelectedClientId}
                    />
                  </div>
                )}
                
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-gray-600 uppercase tracking-wide flex items-center gap-1.5">
                    <CalendarIcon className="h-3.5 w-3.5" />
                    Data Início
                  </label>
                  <Popover open={startDateOpen} onOpenChange={setStartDateOpen}>
                    <PopoverTrigger asChild>
                      <Button 
                        variant="outline" 
                        className="w-full justify-start text-left font-normal h-9 text-sm bg-white hover:bg-gray-50 border-gray-300 px-3"
                      >
                        {startDate ? format(startDate, 'dd/MM/yyyy', { locale: ptBR }) : 'Selecione'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={startDate}
                        onSelect={(date) => {
                          setStartDate(date);
                          setStartDateOpen(false);
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-gray-600 uppercase tracking-wide flex items-center gap-1.5">
                    <CalendarIcon className="h-3.5 w-3.5" />
                    Data Fim
                  </label>
                  <Popover open={endDateOpen} onOpenChange={setEndDateOpen}>
                    <PopoverTrigger asChild>
                      <Button 
                        variant="outline" 
                        className="w-full justify-start text-left font-normal h-9 text-sm bg-white hover:bg-gray-50 border-gray-300 px-3"
                      >
                        {endDate ? format(endDate, 'dd/MM/yyyy', { locale: ptBR }) : 'Selecione'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={endDate}
                        onSelect={(date) => {
                          setEndDate(date);
                          setEndDateOpen(false);
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="flex items-center gap-2 col-span-1 md:col-span-2 lg:col-span-2">
                  {user?.role === 'admin' && (
                    <Button
                      variant="outline"
                      onClick={() => setIsCloneModalOpen(true)}
                      className="h-9 text-xs text-gray-600 hover:text-gray-900 hover:bg-gray-100 border-gray-300 px-3 whitespace-nowrap"
                    >
                      <Copy className="h-3.5 w-3.5 mr-1.5" />
                      Clonar Configurações
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setStartDate(new Date(new Date().setMonth(new Date().getMonth() - 1)));
                      setEndDate(new Date());
                      if (user?.role === 'admin') {
                        setSelectedClientId(undefined);
                      }
                    }}
                    className="h-9 text-xs text-gray-600 hover:text-gray-900 hover:bg-gray-100 whitespace-nowrap"
                  >
                    Limpar Filtros
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tabs Elegantes */}
          <Tabs defaultValue="management" className="space-y-6">
            <TabsList className="grid w-full grid-cols-4 bg-white border border-gray-200 shadow-sm h-12 rounded-lg p-1">
              <TabsTrigger 
                value="management" 
                className="flex items-center gap-2 data-[state=active]:bg-gray-900 data-[state=active]:text-white data-[state=active]:shadow-sm rounded-md transition-all duration-200"
              >
                <Users className="h-4 w-4" />
                <span className="font-medium">Gestão</span>
              </TabsTrigger>
              <TabsTrigger 
                value="roi" 
                className="flex items-center gap-2 data-[state=active]:bg-gray-900 data-[state=active]:text-white data-[state=active]:shadow-sm rounded-md transition-all duration-200"
              >
                <DollarSign className="h-4 w-4" />
                <span className="font-medium">ROI</span>
              </TabsTrigger>
              <TabsTrigger 
                value="financial" 
                className="flex items-center gap-2 data-[state=active]:bg-gray-900 data-[state=active]:text-white data-[state=active]:shadow-sm rounded-md transition-all duration-200"
              >
                <TrendingUp className="h-4 w-4" />
                <span className="font-medium">Financeiro</span>
              </TabsTrigger>
              <TabsTrigger 
                value="structure" 
                className="flex items-center gap-2 data-[state=active]:bg-gray-900 data-[state=active]:text-white data-[state=active]:shadow-sm rounded-md transition-all duration-200"
              >
                <Building2 className="h-4 w-4" />
                <span className="font-medium">Estrutura</span>
              </TabsTrigger>
            </TabsList>

            <div className="space-y-6">
              <TabsContent value="management" className="space-y-6 mt-0">
                {effectiveClientId ? (
                  <InteractionManagement clientId={effectiveClientId} />
                ) : (
                  <Card className="border border-gray-200 shadow-sm">
                    <CardContent className="p-16 text-center">
                      <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500 text-base">Selecione um cliente para visualizar a gestão de atendimentos</p>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              <TabsContent value="roi" className="space-y-6 mt-0">
                {effectiveClientId ? (
                  <ROIDashboard
                    clientId={effectiveClientId}
                    startDate={startDateStr}
                    endDate={endDateStr}
                  />
                ) : (
                  <Card className="border border-gray-200 shadow-sm">
                    <CardContent className="p-16 text-center">
                      <DollarSign className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500 text-base">Selecione um cliente para visualizar o dashboard de ROI</p>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              <TabsContent value="financial" className="space-y-6 mt-0">
                <FinancialEventsTable clientId={effectiveClientId} />
              </TabsContent>

              <TabsContent value="structure" className="space-y-6 mt-0">
                {effectiveClientId ? (
                  <OrganizationalStructureComponent clientId={effectiveClientId} />
                ) : (
                  <Card className="border border-gray-200 shadow-sm">
                    <CardContent className="p-16 text-center">
                      <Building2 className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500 text-base">Selecione um cliente para visualizar a estrutura organizacional</p>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>
            </div>
          </Tabs>
        </div>
      </div>

      {/* Modal de Clonar Configurações */}
      {user?.role === 'admin' && (
        <CloneSettingsModal
          isOpen={isCloneModalOpen}
          onClose={() => setIsCloneModalOpen(false)}
          onSuccess={() => {
            // Recarregar dados se necessário
            console.log('Configurações clonadas com sucesso');
          }}
        />
      )}
    </Layout>
  );
};

export default BusinessIntelligence;
